/**
 * The Zoho Learn article for a retired device. Pure: built from the snapshot
 * that retire_rack_unit() stored (migration 20261002160000), so the article
 * says exactly what the survey knew when the device left, and nothing else.
 *
 * PazPortal does not write to Learn. Staff copy the title and body into the
 * property's manual, in the chapter named here, as a draft; they publish after
 * review (a published article reaches the customer's Documents through
 * zoho-learn-sync), then record its link on the retirement.
 *
 * Pinned by `tests/deviceRetirement.test.ts`.
 */

export const RETIREMENT_REASONS = ['failed', 'replaced', 'upgraded', 'removed', 'returned', 'other'] as const;
export type RetirementReason = (typeof RETIREMENT_REASONS)[number];

export const RETIREMENT_DISPOSITIONS = [
  'recycled', 'returned_to_vendor', 'rma', 'stored_on_site', 'stored_off_site', 'customer_kept', 'unknown',
] as const;
export type RetirementDisposition = (typeof RETIREMENT_DISPOSITIONS)[number];

export const REASON_LABEL: Record<RetirementReason, string> = {
  failed: 'Failed',
  replaced: 'Replaced',
  upgraded: 'Upgraded',
  removed: 'Removed (no replacement)',
  returned: 'Returned to owner',
  other: 'Other',
};

export const DISPOSITION_LABEL: Record<RetirementDisposition, string> = {
  recycled: 'Recycled / e-waste',
  returned_to_vendor: 'Returned to vendor',
  rma: 'Sent for RMA',
  stored_on_site: 'Stored on site',
  stored_off_site: 'Stored off site',
  customer_kept: 'Kept by the customer',
  unknown: 'Not recorded',
};

export interface RetirementSnapshot {
  unit: {
    id: string;
    label: string;
    detail: string | null;
    kind: string;
    confidence: string;
    face: 'front' | 'rear';
    ru_start: number;
    ru_height: number;
    ip_address: string | null;
    mac_address: string | null;
    owner: string | null;
    check_note: string | null;
    asset_id: string | null;
  };
  rack: { id: string; code: string; name: string };
  room: { id: string; code: string; name: string | null; role: string };
  location: { id: string; name: string; property_code: string | null };
  networks: Array<{ code: string; name: string; role: string | null; confidence: string }>;
  links: Array<{
    medium: string;
    label: string | null;
    note: string | null;
    confidence: string;
    other_rack: string | null;
    other_unit: string | null;
    other_ru: number | null;
  }>;
}

export interface RetirementRow {
  id: string;
  location_id: string;
  unit_id: string | null;
  retired_on: string;
  reason: RetirementReason;
  disposition: RetirementDisposition;
  replacement: string | null;
  note: string | null;
  snapshot: RetirementSnapshot;
  learn_article_url: string | null;
  learn_filed_at: string | null;
  created_at: string;
}

/**
 * The manual chapter (= customer Documents section) a retired device belongs
 * in. Active network gear → Network Devices; hosts → Computer Assets; the
 * passive plant and power that make up a rack → Rack Schedules.
 */
export function chapterForKind(kind: string): string {
  switch (kind) {
    case 'server':
    case 'kvm':
      return 'Computer Assets';
    case 'patch_panel':
    case 'fiber':
    case 'shelf':
    case 'ups':
    case 'pdu':
      return 'Rack Schedules';
    case 'phone_system':
      return 'Telephony';
    default:
      return 'Network Devices';
  }
}

/** Manual slug from a property's Learn link (share or internal), else null. */
export function manualSlugFromLearnUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = /\/manual\/([^/?#]+)/.exec(url);
  return m ? decodeURIComponent(m[1]) : null;
}

/** "HDC — Retired: Netgear ProSAFE GS728TPP (2026-10-02)", the manual's house style for dated records. */
export function retirementTitle(r: Pick<RetirementRow, 'retired_on' | 'snapshot'>): string {
  const code = r.snapshot.location.property_code ?? r.snapshot.location.name;
  return `${code} — Retired: ${r.snapshot.unit.label} (${r.retired_on})`;
}

function ru(u: RetirementSnapshot['unit']): string {
  const end = u.ru_start + u.ru_height - 1;
  const base = end > u.ru_start ? `U${u.ru_start}–${end}` : `U${u.ru_start}`;
  return u.face === 'rear' ? `${base} rear` : base;
}

/** "MDF · Main distribution · DR2 · U37". */
export function retiredFrom(s: RetirementSnapshot): string {
  const room = s.room.name ? `${s.room.code} · ${s.room.name}` : s.room.code;
  return `${room} · ${s.rack.code} · ${ru(s.unit)}`;
}

const NOT_RECORDED = 'not recorded';

function rows(r: RetirementRow): Array<[string, string]> {
  const u = r.snapshot.unit;
  // Addresses appear only when recorded; an unrecorded one is left out, not placeheld.
  const addresses: Array<[string, string]> = [];
  if (u.ip_address) addresses.push(['IP address', u.ip_address]);
  if (u.mac_address) addresses.push(['MAC address', u.mac_address]);
  return [
    ['Device', u.label],
    ['Description', u.detail ?? NOT_RECORDED],
    ['Kind', u.kind.replace(/_/g, ' ')],
    ['Retired from', retiredFrom(r.snapshot)],
    ...addresses,
    ['Owner', u.owner ?? NOT_RECORDED],
    ['Survey confidence', u.confidence],
    ['Retired on', r.retired_on],
    ['Reason', REASON_LABEL[r.reason]],
    ['Disposition', DISPOSITION_LABEL[r.disposition]],
    ['Replaced by', r.replacement ?? '—'],
  ];
}

function linkLine(l: RetirementSnapshot['links'][number]): string {
  const far = l.other_unit
    ? `${l.other_rack ?? '?'} · U${l.other_ru} · ${l.other_unit}`
    : `${l.other_rack ?? 'unknown rack'} (rack only)`;
  const tag = l.label ? ` — ${l.label}` : '';
  return `${l.medium.replace(/_/g, ' ')} to ${far}${tag} (${l.confidence})`;
}

function networkLine(n: RetirementSnapshot['networks'][number]): string {
  return `${n.name}${n.role ? ` — ${n.role}` : ''} (${n.confidence})`;
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Body for the Learn editor: pastes as headings, a table and lists. */
export function retirementArticleHtml(r: RetirementRow): string {
  const s = r.snapshot;
  const out: string[] = [];
  out.push(`<p>${esc(s.unit.label)} was retired from ${esc(retiredFrom(s))} at ${esc(s.location.name)} on ${esc(r.retired_on)}. The position is now recorded as vacant.</p>`);
  out.push('<h2>Device</h2>');
  out.push('<table><tbody>');
  for (const [k, v] of rows(r)) out.push(`<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`);
  out.push('</tbody></table>');
  out.push('<h2>Networks it served</h2>');
  out.push(s.networks.length ? `<ul>${s.networks.map((n) => `<li>${esc(networkLine(n))}</li>`).join('')}</ul>` : '<p>None recorded.</p>');
  out.push('<h2>Connections removed</h2>');
  out.push(s.links.length ? `<ul>${s.links.map((l) => `<li>${esc(linkLine(l))}</li>`).join('')}</ul>` : '<p>None recorded.</p>');
  if (r.note) out.push('<h2>Notes</h2>', `<p>${esc(r.note)}</p>`);
  if (s.unit.check_note) out.push(`<p><strong>Open check at retirement:</strong> ${esc(s.unit.check_note)}</p>`);
  out.push(`<p><em>Recorded in PazPortal (retirement ${esc(r.id)}). Values are as surveyed when the device was retired.</em></p>`);
  return out.join('\n');
}

/** The same article as plain text, for editors that will not take HTML. */
export function retirementArticleText(r: RetirementRow): string {
  const s = r.snapshot;
  const out: string[] = [
    `${s.unit.label} was retired from ${retiredFrom(s)} at ${s.location.name} on ${r.retired_on}. The position is now recorded as vacant.`,
    '',
    'DEVICE',
    ...rows(r).map(([k, v]) => `${k}: ${v}`),
    '',
    'NETWORKS IT SERVED',
    ...(s.networks.length ? s.networks.map((n) => `- ${networkLine(n)}`) : ['None recorded.']),
    '',
    'CONNECTIONS REMOVED',
    ...(s.links.length ? s.links.map((l) => `- ${linkLine(l)}`) : ['None recorded.']),
  ];
  if (r.note) out.push('', 'NOTES', r.note);
  if (s.unit.check_note) out.push('', `Open check at retirement: ${s.unit.check_note}`);
  out.push('', `Recorded in PazPortal (retirement ${r.id}). Values are as surveyed when the device was retired.`);
  return out.join('\n');
}
